import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "..";

// Types
export type SortType = "asc" | "desc" | "none";

export interface ProviderBrowserState {
  // Data
  items: any[];
  isLoading: boolean;
  error: string;

  // Selection (Record for O(1) lookup)
  selectedIds: Record<string, boolean>;
  initialSelectedIds: Record<string, boolean>;

  // Filters
  columnFilters: Record<string, string>;
  allDistinctValues: Record<string, string[]>;
  filteredDistinctValues: Record<string, string[]>;

  // Sort
  sortKey: string;
  sortType: SortType;

  // UI
  focusedIndex: number;
}

const initialState: ProviderBrowserState = {
  items: [],
  isLoading: false,
  error: "",
  selectedIds: {},
  initialSelectedIds: {},
  columnFilters: {},
  allDistinctValues: {},
  filteredDistinctValues: {},
  sortKey: "",
  sortType: "none",
  focusedIndex: -1,
};

// Slice
const providerBrowserSlice = createSlice({
  name: "providerBrowser",
  initialState,
  reducers: {
    // Data actions
    fetchStart: (state) => {
      state.isLoading = true;
      state.error = "";
      state.items = [];
    },
    fetchSuccess: (state, action: PayloadAction<any[]>) => {
      state.isLoading = false;
      state.items = action.payload;
    },
    fetchError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Selection actions
    initializeSelection: (state, action: PayloadAction<string[]>) => {
      state.selectedIds = {};
      state.initialSelectedIds = {};
      action.payload.forEach((id) => {
        state.selectedIds[id] = true;
        state.initialSelectedIds[id] = true;
      });
    },
    toggleSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedIds[id]) {
        delete state.selectedIds[id];
      } else {
        state.selectedIds[id] = true;
      }
    },
    setSelection: (state, action: PayloadAction<string>) => {
      state.selectedIds = { [action.payload]: true };
    },
    clearSelection: (state) => {
      state.selectedIds = {};
    },

    // Filter actions
    setColumnFilter: (state, action: PayloadAction<{ column: string; value: string }>) => {
      const { column, value } = action.payload;
      state.columnFilters[column] = value;
    },
    toggleDistinctFilter: (state, action: PayloadAction<{ column: string; value: string }>) => {
      const { column, value } = action.payload;
      const current = state.filteredDistinctValues[column] || [];
      if (current.includes(value)) {
        state.filteredDistinctValues[column] = current.filter((v) => v !== value);
      } else {
        state.filteredDistinctValues[column] = [...current, value];
      }
    },
    setDistinctValues: (
      state,
      action: PayloadAction<{ all: Record<string, string[]>; filtered: Record<string, string[]> }>,
    ) => {
      state.allDistinctValues = action.payload.all;
      state.filteredDistinctValues = action.payload.filtered;
    },

    // Sort actions
    setSort: (state, action: PayloadAction<string>) => {
      const label = action.payload;
      if (state.sortKey !== label) {
        state.sortKey = label;
        state.sortType = "asc";
      } else {
        // Cycle: none -> asc -> desc -> none
        const nextType: SortType = state.sortType === "none" ? "asc" : state.sortType === "asc" ? "desc" : "none";
        state.sortType = nextType;
      }
    },
    initializeSort: (state, action: PayloadAction<{ key: string; type: SortType }>) => {
      state.sortKey = action.payload.key;
      state.sortType = action.payload.type;
    },

    // UI actions
    setFocusedIndex: (state, action: PayloadAction<number>) => {
      state.focusedIndex = action.payload;
    },

    // Reset
    reset: () => initialState,
  },
});

// Actions
export const {
  fetchStart,
  fetchSuccess,
  fetchError,
  initializeSelection,
  toggleSelection,
  setSelection,
  clearSelection,
  setColumnFilter,
  toggleDistinctFilter,
  setDistinctValues,
  setSort,
  initializeSort,
  setFocusedIndex,
  reset,
} = providerBrowserSlice.actions;

// Base selectors
export const selectProviderBrowser = (state: RootState) => state.providerBrowser;
export const selectItems = (state: RootState) => state.providerBrowser.items;
export const selectIsLoading = (state: RootState) => state.providerBrowser.isLoading;
export const selectError = (state: RootState) => state.providerBrowser.error;
export const selectSelectedIds = (state: RootState) => state.providerBrowser.selectedIds;
export const selectColumnFilters = (state: RootState) => state.providerBrowser.columnFilters;
export const selectAllDistinctValues = (state: RootState) => state.providerBrowser.allDistinctValues;
export const selectFilteredDistinctValues = (state: RootState) => state.providerBrowser.filteredDistinctValues;
export const selectSortKey = (state: RootState) => state.providerBrowser.sortKey;
export const selectSortType = (state: RootState) => state.providerBrowser.sortType;
export const selectFocusedIndex = (state: RootState) => state.providerBrowser.focusedIndex;

// Memoized selector: selected IDs as Set for O(1) lookup
export const selectSelectedIdsSet = createSelector([selectSelectedIds], (selectedIds) => {
  return new Set(Object.keys(selectedIds));
});

// Memoized selector: selected values as array
export const selectSelectedValues = createSelector([selectSelectedIds], (selectedIds) => {
  return Object.keys(selectedIds);
});

// Initial selected IDs selectors
export const selectInitialSelectedIds = (state: RootState) => state.providerBrowser.initialSelectedIds;

export const selectInitialSelectedIdsSet = createSelector([selectInitialSelectedIds], (initialSelectedIds) => {
  return new Set(Object.keys(initialSelectedIds));
});

// Reducer export
export const providerBrowserReducer = providerBrowserSlice.reducer;
